/**
 * AllTracersModal — run every registered chart-tracing handler on one crop
 * and let the operator eyeball each overlay before picking one to hand off to
 * WPD.  Same promote-to-Chart + hitl-init hop as the single-tracer "Open in
 * WPD" button; the only difference is which handler's stored trace is used.
 */
import { useEffect, useRef, useState } from "react";

import { api } from "../../api/client";
import type { AllTracersHandler, AllTracersManifest } from "../../api/client";

interface Props {
  path: string;
  jobId?: string;
  filename: string;
  onClose: () => void;
}

export function AllTracersModal({ path, jobId, filename, onClose }: Props) {
  const [manifest, setManifest] = useState<AllTracersManifest | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  // Enqueue the run once on mount, then poll the manifest.
  useEffect(() => {
    let alive = true;

    const kick = async () => {
      if (started.current) return;
      started.current = true;
      try {
        // Load any prior run first — if it's still fresh, skip the enqueue
        // so the operator keeps their previous selection.
        const existing = await api.batch.getAllTracers(path, filename, jobId);
        if (existing.status === "done" && (existing.handlers?.length ?? 0) > 0) {
          if (alive) setManifest(existing);
          return;
        }
        await api.batch.startAllTracers(path, filename, jobId);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    };

    void kick();

    const tick = async () => {
      try {
        const m = await api.batch.getAllTracers(path, filename, jobId);
        if (!alive) return;
        setManifest(m);
      } catch {
        // transient poll error — try again next tick
      }
    };
    void tick();
    const iv = window.setInterval(() => {
      if (manifest?.status === "done" || manifest?.status === "failed") return;
      void tick();
    }, 1500);

    return () => {
      alive = false;
      window.clearInterval(iv);
    };
    // path / filename / jobId only change on a fresh open, and the poll
    // deliberately re-reads `manifest` on every tick — don't put it in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, filename, jobId]);

  const handlers = manifest?.handlers ?? [];
  const total = manifest?.handler_count ?? 0;
  const running = handlers.length;
  const isDone = manifest?.status === "done";
  const failed = manifest?.status === "failed";
  const currentHandler =
    !isDone && !failed && total > 0 && running < total
      ? `#${running + 1} of ${total}`
      : null;

  const openInWpd = async () => {
    if (!selected) return;
    // Open the tab synchronously inside the click handler so the browser
    // treats it as user-initiated (see PanelCrop.openInWpd for why).
    const win = window.open("about:blank", "_blank");
    if (!win) {
      alert("Popup blocked — allow popups for this site and try again.");
      return;
    }
    setOpening(true);
    try {
      const r = await api.batch.selectAllTracersHandler(
        path, filename, selected, jobId,
      );
      const init = await api.hitl.init(r.job_id, r.chart_id);
      win.location.href = init.wpd_editor_url;
      onClose();
    } catch (e) {
      win.close();
      alert(`Failed to open in WPD: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOpening(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-rdp-panel border border-rdp-border rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-rdp-border">
          <div>
            <h2 className="text-slate-200 font-medium">Try all tracers · {filename}</h2>
            <p className="text-[11px] text-slate-500">
              Each tile is one handler's guess.  Pick the overlay whose curves match the chart, then Open in WPD.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-xl px-2"
          >
            ✕
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-4 py-2 border-b border-rdp-border bg-rdp-bg/40 text-xs text-slate-400 flex items-center gap-3">
          {error ? (
            <span className="text-red-400">✗ {error}</span>
          ) : failed ? (
            <span className="text-red-400">✗ Run failed: {manifest?.error ?? "unknown"}</span>
          ) : isDone ? (
            <span className="text-emerald-400">✓ All {total} handlers finished.  Pick one below.</span>
          ) : (
            <>
              <span className="animate-pulse text-blue-400">●</span>
              <span>
                Running handler {currentHandler ?? `${running}/${total || "?"}`}
                {total > 0 && ` — ${Math.round((running / total) * 100)}% done`}
              </span>
            </>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto p-4">
          {handlers.length === 0 ? (
            <p className="text-slate-500 text-sm">Warming up…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {handlers.map((h) => (
                <HandlerTile
                  key={h.name}
                  handler={h}
                  selected={selected === h.name}
                  onSelect={() => h.status === "ok" && setSelected(h.name)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-rdp-border px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {selected
              ? <>Selected: <span className="text-emerald-300">{selected}</span></>
              : "Click a tile to select a handler."}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
            >
              Close
            </button>
            <button
              onClick={openInWpd}
              disabled={!selected || opening}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {opening ? "Opening…" : "🔗 Open selected in WPD"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function HandlerTile({
  handler,
  selected,
  onSelect,
}: {
  handler: AllTracersHandler;
  selected: boolean;
  onSelect: () => void;
}) {
  const ok = handler.status === "ok";
  const border =
    selected
      ? "border-emerald-500 ring-2 ring-emerald-500/40"
      : ok
        ? "border-rdp-border hover:border-slate-500"
        : "border-red-900/40";

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!ok}
      className={`text-left bg-rdp-bg/60 border rounded overflow-hidden transition ${border} ${ok ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
    >
      <div className="aspect-[4/3] bg-black/40 flex items-center justify-center overflow-hidden">
        {handler.overlay_url ? (
          <img
            src={handler.overlay_url}
            alt={`${handler.name} overlay`}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : ok ? (
          <span className="text-slate-600 text-xs">no overlay</span>
        ) : (
          <span className="text-red-500/70 text-xs px-2 text-center">✗ {handler.error?.slice(0, 80) ?? "failed"}</span>
        )}
      </div>
      <div className="p-2 space-y-0.5">
        <p className={`text-[11px] font-medium ${ok ? "text-slate-200" : "text-red-400"}`}>
          {handler.name}
        </p>
        {ok && (
          <p className="text-[10px] text-slate-500">
            {handler.curve_count ?? 0} curves
            {(handler.families_count ?? 0) > 0 && ` · ${handler.families_count} families`}
          </p>
        )}
        {ok && handler.notes && handler.notes.length > 0 && (
          <p className="text-[9px] text-slate-600 italic truncate" title={handler.notes.join(" · ")}>
            {handler.notes.join(" · ")}
          </p>
        )}
      </div>
    </button>
  );
}
